import { Planner } from '@deltasierra/shared';
import * as React from 'react';
import styled from 'styled-components';
import { OneWayBinding, OptionalExpressionBinding } from '../../../common/angularData';
import { useAngularServiceContext } from '../../../common/componentUtils/angularServiceContexts';
import { withAngularIntegration } from '../../../common/componentUtils/reactComponentRegistration';
import { PlatformLogo } from '../../../platforms/components/PlatformLogo';
import { PlannerItemThumbnailContainer } from './PlannerItemThumbnailContainer';

const StyledDiv = styled.div`
    position: relative;
`;

export type PlannerItemProps = {
    dragging: boolean;
    onClickItem?: (event: unknown) => void;
    planner: Planner;
};

export function PlannerItem({ dragging, onClickItem, planner }: PlannerItemProps) {
    const anchorRef = React.useRef<HTMLDivElement>(null);

    const plannerUIService = useAngularServiceContext('PlannerUIService');

    const [open, setOpen] = React.useState(false);

    const plannerItemClass = React.useMemo(
        () => plannerUIService.getPlannerLegendClassName(planner),
        [plannerUIService, planner],
    );

    const plannerIconName = React.useMemo(
        () => plannerUIService.getPlannerLegendIconName(planner),
        [plannerUIService, planner],
    );

    const plannerIconFallbackFaClassName = React.useMemo(
        () => plannerUIService.getPlannerLegendIconClassName(planner),
        [plannerUIService, planner],
    );

    const onMouseEnter = React.useCallback(() => {
        if (dragging) {
            return;
        }
        setOpen(true);
    }, [dragging]);

    const onMouseLeave = React.useCallback(() => {
        setOpen(false);
    }, []);

    // We need to hide the popper when dragging starts, otherwise it looks ugly
    React.useEffect(() => {
        if (dragging) {
            setOpen(false);
        }
    }, [dragging]);

    return (
        <StyledDiv onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <div
                className={`cal-item draggable ${plannerItemClass}`}
                data-cy="planner-event-title"
                ref={anchorRef}
                onClick={onClickItem}
            >
                <PlatformLogo fallbackFaClassName={plannerIconFallbackFaClassName} name={plannerIconName} size={15} />
                <span className="title">{planner.title}</span>
            </div>
            <PlannerItemThumbnailContainer anchorEl={anchorRef.current} open={open} plannerId={planner.graphqlId} />
        </StyledDiv>
    );
}
PlannerItem.displayName = 'PlannerItem';

export const PlannerItemWithAngular = withAngularIntegration(PlannerItem, 'plannerItem', {
    dragging: OneWayBinding,
    onClickItem: OptionalExpressionBinding,
    planner: OneWayBinding,
});
